import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface KnowledgeArticle {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  content: string;
  category: string;
  version: string | null;
  icon: string | null;
  highlights: string[];
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface CreateArticleInput {
  slug: string;
  title: string;
  description?: string;
  content: string;
  category?: string;
  version?: string;
  icon?: string;
  highlights?: string[];
  published?: boolean;
}

export interface UpdateArticleInput extends Partial<CreateArticleInput> {
  id: string;
}

export function useKnowledgeArticles() {
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchArticles = async (includeUnpublished = false) => {
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from("knowledge_articles")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (!includeUnpublished) {
        query = query.eq("published", true);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const formattedArticles: KnowledgeArticle[] = (data || []).map(article => ({
        ...article,
        highlights: Array.isArray(article.highlights) ? article.highlights as string[] : []
      }));

      setArticles(formattedArticles);
    } catch (err: any) {
      setError(err.message);
      console.error("Error fetching articles:", err);
    } finally {
      setLoading(false);
    }
  };

  const createArticle = async (input: CreateArticleInput): Promise<KnowledgeArticle | null> => {
    try {
      const { data, error: createError } = await supabase
        .from("knowledge_articles")
        .insert({
          slug: input.slug,
          title: input.title,
          description: input.description || null,
          content: input.content,
          category: input.category || "General",
          version: input.version || null,
          icon: input.icon || "FileText",
          highlights: input.highlights || [],
          published: input.published || false,
          published_at: input.published ? new Date().toISOString() : null,
          created_by: user?.id || null
        })
        .select()
        .single();

      if (createError) throw createError;

      toast.success("Article created successfully");
      await fetchArticles(true);
      
      return {
        ...data,
        highlights: Array.isArray(data.highlights) ? data.highlights as string[] : []
      };
    } catch (err: any) {
      toast.error("Failed to create article: " + err.message);
      console.error("Error creating article:", err);
      return null;
    }
  };

  const updateArticle = async (input: UpdateArticleInput): Promise<boolean> => {
    try {
      const updateData: any = {};
      
      if (input.slug !== undefined) updateData.slug = input.slug;
      if (input.title !== undefined) updateData.title = input.title;
      if (input.description !== undefined) updateData.description = input.description;
      if (input.content !== undefined) updateData.content = input.content;
      if (input.category !== undefined) updateData.category = input.category;
      if (input.version !== undefined) updateData.version = input.version;
      if (input.icon !== undefined) updateData.icon = input.icon;
      if (input.highlights !== undefined) updateData.highlights = input.highlights;
      if (input.published !== undefined) {
        updateData.published = input.published;
        if (input.published) {
          updateData.published_at = new Date().toISOString();
        }
      }

      const { error: updateError } = await supabase
        .from("knowledge_articles")
        .update(updateData)
        .eq("id", input.id);

      if (updateError) throw updateError;

      toast.success("Article updated successfully");
      await fetchArticles(true);
      return true;
    } catch (err: any) {
      toast.error("Failed to update article: " + err.message);
      console.error("Error updating article:", err);
      return false;
    }
  };

  const deleteArticle = async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from("knowledge_articles")
        .delete()
        .eq("id", id);

      if (deleteError) throw deleteError;

      toast.success("Article deleted successfully");
      await fetchArticles(true);
      return true;
    } catch (err: any) {
      toast.error("Failed to delete article: " + err.message);
      console.error("Error deleting article:", err);
      return false;
    }
  };

  const togglePublish = async (id: string, publish: boolean): Promise<boolean> => {
    return updateArticle({
      id,
      published: publish
    });
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  return {
    articles,
    loading,
    error,
    fetchArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    togglePublish
  };
}
